import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { NewDropoffOpenCloseTime, NewDropoffOpeningHours } from '../../../../core/domain/models/NewDropoff'
import NavigationButtons from '../../NavigationButtons'
import DaysOfWeekSelector from '../openingHours/DaysOfWeekSelector'
import Checkbox from './Checkbox'
import OpenCloseEntry from './OpenCloseEntry'

export interface ISubmitDropoffOpeningHoursProps {
    onDismiss: () => void
    onValidate: (openingHours: NewDropoffOpeningHours) => void
    currentOpeningHours?:  NewDropoffOpeningHours
}

const SubmitDropoffOpeningHours: React.FC<ISubmitDropoffOpeningHoursProps> = ({
    onDismiss,
    onValidate,
    currentOpeningHours
}) => {
    const intl = useIntl()
    const getTimezone: () => string = () => Intl.DateTimeFormat().resolvedOptions().timeZone

    const [openingHours, setOpeningHours] = useState<NewDropoffOpeningHours>(currentOpeningHours || {
        isOpenAllDay: false,
        daysOfWeek: [],
        openCloseTimes: [],
        timezone: getTimezone()
    })

    // Display errors after first click on validate or if current values already have errors
    const [shouldDisplayErrors, setShouldDisplayErrors] = useState<boolean>(currentOpeningHours !== undefined)

    const onValidateClick = () => {
        // Check that all fields are valid. After first click on validate, display errors
        let areTimesValid = true
        openingHours.openCloseTimes.forEach(entry => {
            areTimesValid = areTimesValid && isTimeEntryValid(entry)
        })

        if (isDaysSelectionValid && areTimesValid) {
            onValidate(openingHours)
        } else {
            setShouldDisplayErrors(true)
        }
    }

    const isTimeEntryValid: (entry: NewDropoffOpenCloseTime) => boolean = (entry) => {
        // Invalid if empty of close time is before open time
        if (entry.openTime.length === 0 || entry.closeTime.length === 0 || entry.openTime >= entry.closeTime) {
            return false
        }
        return true
    }

    // If a time is selected or checked opened all day -> must select at least a day
    const isDaysSelectionValid = !openingHours
        || openingHours.daysOfWeek.length > 0
        || (openingHours.openCloseTimes.length === 0 && !openingHours.isOpenAllDay)
        || (openingHours.openCloseTimes.length > 0 && openingHours.daysOfWeek.length > 0)
        || (openingHours.isOpenAllDay && openingHours.daysOfWeek.length > 0)

    return (
        <SubmitDropoffOpeningHoursContainer>
            <DaysOfWeekSelector
                daysOfWeek={openingHours.daysOfWeek}
                setDaysOfWeek={
                    (newDaysOfWeek: number[]) => { 
                        setOpeningHours({
                            ...openingHours,
                            daysOfWeek: newDaysOfWeek
                        }) 
                    }
                }
                hasError={shouldDisplayErrors && !isDaysSelectionValid}
            />
            <CheckboxContent>
                <Checkbox
                    isSelected={openingHours.isOpenAllDay}
                    setIsSelected={(isChecked: boolean) => {
                        // Selecting this removes the open/close time selection
                        setOpeningHours({
                            ...openingHours,
                            isOpenAllDay: isChecked,
                            openCloseTimes: []
                        })
                    }}
                />
                <CheckboxLabel>
                    {intl.formatMessage({id: 'SubmitDropoff.contact.open.allday'})}
                </CheckboxLabel>
            </CheckboxContent>
            {
                !openingHours.isOpenAllDay && (
                    <TimeEntriesContainer>
                        {
                            openingHours?.openCloseTimes.map((value, index) => {
                                return (
                                    <OpenCloseEntry
                                        key={index}
                                        entry={value}
                                        onDelete={
                                            (() => {
                                                const newOpenCloseTime = [...openingHours.openCloseTimes]
                                                newOpenCloseTime.splice(index, 1)
                                                setOpeningHours({
                                                    ...openingHours,
                                                    openCloseTimes: newOpenCloseTime
                                                })
                                            })
                                        }
                                        onUpdate={
                                            (newValue) => {
                                                const newOpenCloseTime = [...openingHours.openCloseTimes]
                                                newOpenCloseTime[index] = newValue
                                                setOpeningHours({
                                                    ...openingHours,
                                                    openCloseTimes: newOpenCloseTime
                                                })
                                            }
                                        }
                                        hasError={shouldDisplayErrors && !isTimeEntryValid(value)}
                                    />
                                )
                            })
                        }
                        {
                            openingHours.openCloseTimes.length < 4 && (
                                <AddEntryButton
                                    onClick={() => {
                                        setOpeningHours({
                                            ...openingHours,
                                            openCloseTimes: [...openingHours.openCloseTimes, {
                                                openTime: '',
                                                closeTime: ''
                                            }]
                                        })
                                    }}
                                >
                                    {intl.formatMessage({id: 'SubmitDropoff.contact.add.hours'})}
                                </AddEntryButton>
                            )
                        }
                        
                    </TimeEntriesContainer>
                )
            }
            <NavigationButtons
                previousTitle={intl.formatMessage({id: 'SubmitDropoff.navigation.cancel'})}
                nextTitle={intl.formatMessage({id: 'SubmitDropoff.navigation.validate'})}
                onPreviousClick={onDismiss}
                onNextClick={onValidateClick}
            />
        </SubmitDropoffOpeningHoursContainer>
    )
}

const SubmitDropoffOpeningHoursContainer = styled.div`
    display: flex;
    flex-direction: column;
    padding-top: 14px;
`

const CheckboxContent = styled.div`
    flex: 1;
    display: flex;
    flex-direction: row;
    align-items: center;
    margin-top: 14px;
    margin-bottom: 14px;
`

const CheckboxLabel = styled.div`
    margin-left: 8px;
    font-size: ${({ theme }) => theme.size.small};
    font-family: ${({ theme }) => theme.font.default};
    color: ${({ theme }) => theme.color.black};
    font-weight: ${({ theme }) => theme.weight.normal};
`

const TimeEntriesContainer = styled.div`
    display: flex;
    flex-direction: column;
`

const AddEntryButton = styled.button`
    padding: 24px;
    font-size: ${({ theme }) => theme.size.small};
    font-family: ${({ theme }) => theme.font.default};
    color: ${({ theme }) => theme.color.primary};
    font-weight: ${({ theme }) => theme.weight.normal};
    cursor: pointer;
`

export default SubmitDropoffOpeningHours
